import { useEffect, useState, type ReactNode } from 'react';

import { ErroreApi } from '../api/client.ts';
import {
  categorie as apiCategorie,
  scadenze as apiScadenze,
  soggetti as apiSoggetti,
  tipiScadenza as apiTipi,
  utenti as apiUtenti,
} from '../api/scadenziario.ts';
import type {
  EffettoEliminazione,
  EsitoEliminazione,
  ImpattoEliminazione,
  SceltaCollegate,
  VoceImpatto,
} from '../api/tipi-dati.ts';
import { Dialogo } from './Dialogo.tsx';
import {
  collegateDaMandare,
  destiniCollegate,
  domandaCollegate,
  etichettaConferma,
  messaggioEliminato,
  nomeCombacia,
  puoConfermare,
  serveSceltaCollegate,
} from './eliminazione.ts';

/**
 * Conferma di una cancellazione definitiva. **Uno solo per tutte e quattro le
 * entità**, e non è pigrizia.
 *
 * Il backend risponde a `GET /<entità>/:id/impatto-eliminazione` con la stessa
 * identica forma per scadenze, utenti, soggetti e tipi (`lib/http.ts`), proprio
 * perché questo dialogo potesse essere scritto una volta. Quattro copie
 * significherebbero quattro dialoghi che nel giro di qualche mese si
 * comportano in quattro modi diversi davanti alla stessa domanda — e la
 * domanda, qui, è quella che non si può rifare.
 *
 * ## Cosa fa, nell'ordine
 *
 * 1. All'apertura chiede l'impatto e mostra un caricamento. **Il pulsante rosso
 *    non esiste finché non si sa cosa comporta premerlo.**
 * 2. Se non si può eliminare, il dialogo smette di essere una domanda e diventa
 *    una spiegazione: il `motivo` del backend contiene già i numeri e dice cosa
 *    fare invece. Nessun pulsante rosso, nemmeno disattivato — un pulsante
 *    disattivato invita a cercare il modo di attivarlo.
 * 3. Se si può, si mostra cosa si perde e si chiede di **ricopiare il nome**.
 * 4. Solo per soggetti e tipi che hanno scadenze collegate, e **solo dopo** i
 *    due passi qui sopra, si chiede che fine devono fare quelle scadenze.
 *
 * ## Perché la scelta viene per ultima, e non insieme al resto
 *
 * L'ordine è quello chiesto: avvertimenti, poi il nome ricopiato, poi la
 * scelta. Non è una preferenza estetica. La ricopiatura del nome risponde a
 * «sto cancellando la cosa giusta?»; la scelta risponde a «fin dove arriva la
 * cancellazione?». Sono due domande diverse, e chiederle insieme significa che
 * una delle due viene risposta distrattamente — quasi sempre la seconda, che è
 * quella che decide se dodici anni di storico restano o spariscono.
 *
 * Metterla dopo ha anche un effetto pratico: quando la scelta compare, il nome
 * è già stato ricopiato. Chi arriva alla domanda ci arriva avendo già guardato
 * *quale* riga sta cancellando.
 */

// -----------------------------------------------------------------------------
// Chi si può eliminare
// -----------------------------------------------------------------------------

export type EntitaEliminabile = 'scadenza' | 'soggetto' | 'tipo' | 'categoria' | 'utente';

/**
 * Cosa serve sapere per aprire il dialogo, prima ancora di chiamare il backend.
 *
 * `nome` ed `etichettaNome` li decide **la pagina** e non questo componente: è
 * la pagina a sapere quale colonna l'occhio sta seguendo, ed è quella la parola
 * che va ricopiata. Per una scadenza è il soggetto — «sto cancellando la riga
 * di Rossi o quella di Bianchi?» è l'errore probabile; per un utente è
 * l'email, che è corta, unica e distingue due colleghi omonimi.
 */
export interface BersaglioEliminazione {
  entita: EntitaEliminabile;
  id: number;
  /** Il testo esatto da ricopiare nella casella di conferma. */
  nome: string;
  /** Come si chiama quel testo, per l'etichetta del campo: «l'indirizzo email». */
  etichettaNome: string;
}

/**
 * Le rotte, una riga per entità.
 *
 * Sono tutte funzioni di `api/scadenziario.ts`, quindi qui non si compone
 * nessun URL: se un percorso cambia si corregge in un posto solo, che è poi il
 * motivo per cui quel modulo esiste.
 */
const ROTTE: Record<
  EntitaEliminabile,
  {
    titolo: string;
    /**
     * `collegate` chiede l'impatto **del ramo che si sta guardando**. Le rotte
     * di scadenze e utenti lo ignorano perché non l'hanno mai accettato: sono
     * assegnabili a questa firma con un parametro in meno, ed è giusto così —
     * per loro non c'è nessun ramo da scegliere.
     */
    impatto: (id: number, collegate?: SceltaCollegate) => Promise<ImpattoEliminazione>;
    elimina: (id: number, collegate?: SceltaCollegate) => Promise<EsitoEliminazione>;
  }
> = {
  scadenza: {
    titolo: 'Elimina definitivamente la scadenza',
    impatto: apiScadenze.impattoEliminazione,
    elimina: apiScadenze.elimina,
  },
  soggetto: {
    titolo: 'Elimina definitivamente il soggetto',
    impatto: apiSoggetti.impattoEliminazione,
    elimina: apiSoggetti.elimina,
  },
  tipo: {
    titolo: 'Elimina definitivamente il tipo',
    impatto: apiTipi.impattoEliminazione,
    elimina: apiTipi.elimina,
  },
  categoria: {
    titolo: 'Elimina definitivamente la categoria',
    impatto: apiCategorie.impattoEliminazione,
    elimina: apiCategorie.elimina,
  },
  utente: {
    titolo: "Elimina definitivamente l'utente",
    impatto: apiUtenti.impattoEliminazione,
    elimina: apiUtenti.elimina,
  },
};

// -----------------------------------------------------------------------------
// I quattro effetti, distinguibili senza il colore
// -----------------------------------------------------------------------------

/*
 * ⚠️ Vincolo fisso del progetto: **il colore non è mai l'unico segnale.** Vale
 * per le cinque situazioni delle scadenze e vale qui, dove la differenza fra
 * "sparisce per sempre" e "resta ma perde un collegamento" è la differenza fra
 * un dato perso e un dato ritrovabile.
 *
 * Ogni effetto porta quindi tre segnali indipendenti: una **forma** diversa
 * (cestino, catena spezzata, scudo, divieto), una **parola** scritta per esteso
 * nell'etichetta, e solo in aggiunta un colore. Chi non distingue rosso e
 * ambra legge SPARISCE e RESTA SCOLLEGATO; chi guarda di fretta vede quattro
 * disegni diversi.
 */

interface Presentazione {
  etichetta: string;
  classe: string;
  icona: ReactNode;
}

/** Cestino: quello che viene buttato via. */
const CESTINO = (
  <path d="M2.8 4.6h10.4M6.3 4.6V3.1a.6.6 0 0 1 .6-.6h2.2a.6.6 0 0 1 .6.6v1.5M4.4 4.6l.6 8.2a.7.7 0 0 0 .7.7h4.6a.7.7 0 0 0 .7-.7l.6-8.2M6.6 7v5M9.4 7v5" />
);

/** Catena spezzata: resta, ma il legame no. */
const CATENA_SPEZZATA = (
  <path d="M6.6 9.4 5.1 10.9a2.6 2.6 0 0 1-3.7-3.7l1.5-1.5M9.4 6.6l1.5-1.5a2.6 2.6 0 0 1 3.7 3.7l-1.5 1.5M8 1.4v1.8M1.4 8h1.8M14.6 8h-1.8M8 14.6v-1.8" />
);

/** Scudo con la spunta: quello che regge. */
const SCUDO = (
  <path d="M8 1.6 3.2 3.3v4c0 3 2 5.2 4.8 6.5 2.8-1.3 4.8-3.5 4.8-6.5v-4L8 1.6ZM5.9 7.6l1.5 1.6 2.9-3.1" />
);

/** Divieto: non passa, ed è il motivo per cui non passa. */
const DIVIETO = (
  <path d="M8 1.9a6.1 6.1 0 1 0 0 12.2A6.1 6.1 0 0 0 8 1.9ZM3.7 12.3 12.3 3.7" />
);

const PRESENTAZIONE: Record<EffettoEliminazione, Presentazione> = {
  ELIMINATO: { etichetta: 'Sparisce', classe: 'effetto-eliminato', icona: CESTINO },
  SCOLLEGATO: {
    etichetta: 'Resta scollegato',
    classe: 'effetto-scollegato',
    icona: CATENA_SPEZZATA,
  },
  CONSERVATO: { etichetta: "Resta com'è", classe: 'effetto-conservato', icona: SCUDO },
  IMPEDISCE: { etichetta: 'Blocca la cancellazione', classe: 'effetto-impedisce', icona: DIVIETO },
};

function VoceConseguenza({ voce }: { voce: VoceImpatto }) {
  const p = PRESENTAZIONE[voce.effetto];

  return (
    <li className={`voce-impatto ${p.classe}`}>
      {/* L'icona è decorativa: la stessa informazione è scritta accanto
          nell'etichetta, e leggerla due volte con un lettore di schermo
          sarebbe solo rumore. */}
      <svg
        className="impatto-icona"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {p.icona}
      </svg>

      <div className="impatto-testata">
        <span className="impatto-cosa">
          <span className="impatto-quanti">{voce.quanti}</span> {voce.cosa.toLocaleLowerCase('it')}
        </span>
        <span className="impatto-etichetta">{p.etichetta}</span>
      </div>

      <p className="impatto-spiegazione">{voce.spiegazione}</p>
    </li>
  );
}

// -----------------------------------------------------------------------------
// Errori
// -----------------------------------------------------------------------------

/**
 * Un rifiuto del backend, in italiano.
 *
 * I 409 arrivano già con il messaggio scritto per l'utente — il backend riusa
 * lo stesso `motivo` del preflight (`plugins/errori.ts`) — e questa funzione
 * quasi sempre non fa che ripassarlo. Esiste per il caso in cui non lo facesse:
 * **`SCADENZE_COLLEGATE`, `AUTO_ELIMINAZIONE` e `ULTIMO_AMMINISTRATORE` non
 * devono mai comparire a schermo come codici**, e senza queste tre frasi di
 * riserva basterebbe un messaggio vuoto perché succedesse.
 */
const RISERVE: Record<string, string> = {
  /*
   * ⚠️ Questo codice non blocca più soggetti e tipi: da quando esistono le
   * scadenze orfane si può cancellarli anche con scadenze attaccate, scegliendo
   * che fine fanno. Se arriva ancora, vuol dire che la scelta non è stata
   * mandata — e la frase deve dirlo, non consigliare di spostare le scadenze
   * altrove come faceva prima.
   */
  SCADENZE_COLLEGATE:
    'Ci sono scadenze collegate e non è stato indicato che fine devono fare. Torna indietro e scegli se eliminarle insieme o conservarle nello storico.',
  AUTO_ELIMINAZIONE:
    "Non puoi eliminare te stesso: perderesti l'accesso mentre lo stai usando, e il tuo utente non esisterebbe più per rimediare. Fatti eliminare da un collega.",
  ULTIMO_AMMINISTRATORE:
    "Resterebbe l'applicazione senza nessun amministratore attivo, e non ci sarebbe più modo di crearne uno dall'interfaccia.",
};

function messaggioErrore(e: unknown): string {
  if (!(e instanceof ErroreApi)) return 'Errore imprevisto';

  const testo = e.message.trim();
  const riserva = e.codice === undefined ? undefined : RISERVE[e.codice];

  // Il messaggio del backend vince, purché ci sia e non sia il codice stesso
  // ripetuto tale e quale.
  if (testo !== '' && testo !== e.codice) return testo;
  return riserva ?? 'Non è stato possibile eliminare questo elemento.';
}

// -----------------------------------------------------------------------------
// Il dialogo
// -----------------------------------------------------------------------------

export function DialogoEliminazione({
  bersaglio,
  onChiudi,
  onEliminato,
}: {
  bersaglio: BersaglioEliminazione;
  onChiudi: () => void;
  onEliminato: (messaggio: string) => void;
}) {
  const rotte = ROTTE[bersaglio.entita];

  const [impatto, setImpatto] = useState<ImpattoEliminazione | null>(null);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [ricopiato, setRicopiato] = useState('');
  const [inCorso, setInCorso] = useState(false);

  /** Quale dei due passi è a schermo. Si avanza solo col nome ricopiato. */
  const [passo, setPasso] = useState<'avvertimenti' | 'scelta'>('avvertimenti');

  /**
   * ⚠️ Parte da `null`, e **non da `'SCOLLEGA'`**.
   *
   * Preselezionare il ramo distruttivo sarebbe evidentemente sbagliato, ma
   * preselezionare anche solo quello conservativo trasformerebbe una domanda in
   * un modulo già compilato: si preme il pulsante rosso senza aver letto la
   * domanda, e la scelta l'ha fatta il programma. Quello che il contratto
   * garantisce è che il ramo *descritto* senza parametro sia il meno
   * distruttivo — non che sia già scelto.
   */
  const [scelta, setScelta] = useState<SceltaCollegate | null>(null);

  /*
   * L'impatto si chiede all'apertura, e **di nuovo a ogni cambio di scelta**.
   *
   * La seconda parte non è un lusso: le `conseguenze` descrivono il ramo
   * chiesto, quindi senza il richiamo il dialogo elencherebbe cosa succede
   * conservando le scadenze mentre il pulsante sta per eliminarle. Un elenco
   * che descrive l'altro ramo è peggio di nessun elenco — si legge, ci si
   * fida, ed è sbagliato.
   *
   * Senza scelta si chiede senza parametro: il backend descrive `SCOLLEGA`,
   * che è il ramo meno distruttivo.
   */
  useEffect(() => {
    let annullato = false;
    setInCaricamento(true);

    rotte
      .impatto(bersaglio.id, scelta ?? undefined)
      .then((i) => { if (!annullato) setImpatto(i); })
      .catch((e: unknown) => { if (!annullato) setErrore(messaggioErrore(e)); })
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, [rotte, bersaglio.id, scelta]);

  const combacia = nomeCombacia(ricopiato, bersaglio.nome);

  // La domanda si pone solo dove il contratto la prevede. `eliminabile` falso
  // non arriva nemmeno qui: quel ramo del dialogo non ha passi.
  const serveScelta =
    impatto !== null && impatto.eliminabile && serveSceltaCollegate(bersaglio.entita, impatto);

  /** Il tipo ristretto che serve per scrivere le frasi dei due destini. */
  const entitaCollegabile =
    bersaglio.entita === 'soggetto' || bersaglio.entita === 'tipo' ? bersaglio.entita : null;

  const confermabile = puoConfermare({ nomeRicopiato: combacia, serveScelta, scelta });

  async function conferma() {
    /*
     * Cintura, oltre alle bretelle del `disabled`: se un giorno qualcuno
     * togliesse il `disabled` dal pulsante, la cancellazione resterebbe
     * comunque dietro al nome ricopiato **e** dietro alla scelta fatta.
     */
    if (!confermabile) return;

    setErrore(null);
    setInCorso(true);

    try {
      const esito = await rotte.elimina(bersaglio.id, collegateDaMandare(serveScelta, scelta));
      onEliminato(messaggioEliminato(esito));
    } catch (e) {
      setErrore(messaggioErrore(e));
      setInCorso(false);
    }
  }

  /**
   * L'elenco delle conseguenze, con il titolo che gli spetta nel punto in cui
   * compare.
   *
   * ⚠️ Mentre l'impatto si ricarica l'elenco resta a schermo ma **si dichiara
   * in aggiornamento**: quello che si sta leggendo in quell'istante descrive
   * ancora il ramo di prima. Toglierlo del tutto farebbe saltare l'altezza del
   * dialogo sotto le mani; lasciarlo senza dirlo lo farebbe leggere come
   * aggiornato.
   */
  function elencoConseguenze(titolo: string) {
    if (impatto === null || impatto.conseguenze.length === 0) return null;

    return (
      <>
        <p className="titoletto-impatto">
          {titolo}
          {inCaricamento && <span className="impatto-in-aggiornamento"> · aggiornamento…</span>}
        </p>
        <ul className="elenco-impatto" aria-busy={inCaricamento}>
          {impatto.conseguenze.map((c) => (
            <VoceConseguenza key={`${c.effetto}-${c.cosa}`} voce={c} />
          ))}
        </ul>
      </>
    );
  }

  /**
   * Il pulsante rosso finale, uguale nei due punti in cui può comparire.
   *
   * È una funzione che restituisce JSX, **non un componente**: chiamata come
   * `{azioniFinali()}` produce gli elementi in linea, mentre `<AzioniFinali/>`
   * sarebbe un tipo di componente nuovo a ogni render e React rimonterebbe i
   * pulsanti a ogni tasto digitato, portandosi via il fuoco della tastiera.
   */
  function azioniFinali() {
    return (
      <div className="dialogo-azioni">
        <button
          type="button"
          className="bottone bottone-pericolo-pieno"
          onClick={() => void conferma()}
          // Anche mentre l'impatto si sta ricaricando: per un istante l'elenco
          // a schermo è quello del ramo precedente, e il pulsante non deve
          // poter partire su una descrizione vecchia.
          disabled={inCorso || inCaricamento || !confermabile}
        >
          {inCorso
            ? 'Eliminazione…'
            : etichettaConferma(serveScelta ? impatto?.collegate ?? null : null, scelta)}
        </button>
        <button
          type="button"
          className="bottone-piatto"
          onClick={passo === 'scelta' ? () => setPasso('avvertimenti') : onChiudi}
          disabled={inCorso}
        >
          {passo === 'scelta' ? 'Torna al passo precedente' : 'Torna indietro'}
        </button>
        <p className="aiuto azioni-nota">Non si può annullare.</p>
      </div>
    );
  }

  return (
    <Dialogo titolo={rotte.titolo} aperto onChiudi={onChiudi}>
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      {/* Il caricamento a tutto schermo vale solo per la prima risposta: dal
          secondo giro in poi c'è già qualcosa a schermo, e sostituirlo con
          «Verifica…» a ogni cambio di scelta farebbe sparire e ricomparire il
          dialogo sotto le mani di chi sta scegliendo. */}
      {impatto === null && inCaricamento ? (
        <p className="stato-caricamento">Verifica di cosa comporta la cancellazione…</p>
      ) : impatto === null ? (
        <div className="dialogo-azioni">
          <button type="button" className="bottone" onClick={onChiudi}>Chiudi</button>
        </div>
      ) : (
        <>
          <p className="oggetto-eliminazione">
            <span className="aiuto">Elemento selezionato</span>
            <strong>{impatto.oggetto}</strong>
          </p>

          {!impatto.eliminabile ? (
            /* Non è una domanda: è una spiegazione. Il `motivo` del backend
               contiene già quante sono le cose collegate e cosa fare al posto
               della cancellazione. Nessun passo, nessuna scelta. */
            <>
              <div className="avviso-attenzione" role="note">
                <strong>Non si può eliminare.</strong>
                <p className="aiuto">{impatto.motivo ?? impatto.riepilogo}</p>
              </div>

              {elencoConseguenze('Cosa lo sta bloccando')}

              <div className="dialogo-azioni">
                <button type="button" className="bottone" onClick={onChiudi}>Ho capito</button>
              </div>
            </>
          ) : passo === 'avvertimenti' ? (
            /* --- Primo passo: gli avvertimenti e il nome da ricopiare ------ */
            <>
              <div className="riepilogo-eliminazione" role="note">
                <strong>{impatto.riepilogo}</strong>
              </div>

              {elencoConseguenze('Cosa comporta')}

              {/* ⚠️ Senza questa riga l'elenco qui sopra mentirebbe per
                  omissione: descrive il ramo conservativo, che è quello che il
                  backend racconta quando non gli si chiede niente, mentre la
                  scelta non è ancora stata fatta. Dirlo qui costa una riga; non
                  dirlo significa lasciar credere che sia già deciso. */}
              {serveScelta && (
                <p className="aiuto nota-scelta-dopo">
                  {scelta === null
                    ? 'Le scadenze collegate hanno ancora un destino da decidere: te lo chiediamo al passo dopo, quando avrai confermato quale elemento stai cancellando.'
                    : /* Si torna qui dal passo dopo, con una scelta già fatta:
                         l'elenco qui sopra è quello di quel ramo, e va detto —
                         altrimenti sembrerebbe la descrizione neutra di prima. */
                      'L’elenco qui sopra descrive il destino che hai già scelto al passo successivo. Continua per rivederlo o cambiarlo.'}
                </p>
              )}

              <div className="campo campo-ricopia">
                <label htmlFor="ricopia-nome">
                  Per confermare, ricopia {bersaglio.etichettaNome}
                </label>

                {/* Il nome è mostrato a spaziatura fissa e selezionabile con un
                    clic solo: se contiene accenti, punteggiatura o maiuscole
                    inconsuete, chi lo ricopia deve poter vedere esattamente
                    cos'è. Niente pulsante «copia», però: l'attrito serve a far
                    *guardare quale riga* si sta cancellando, e un copia-incolla
                    lo salterebbe senza aver letto niente. */}
                <p className="nome-da-ricopiare">{bersaglio.nome}</p>

                <input
                  id="ricopia-nome"
                  type="text"
                  value={ricopiato}
                  onChange={(e) => setRicopiato(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  autoFocus
                  aria-describedby="aiuto-ricopia"
                />
                <p className="aiuto" id="aiuto-ricopia">
                  {ricopiato.trim() === ''
                    ? 'Maiuscole e minuscole non contano; gli spazi a inizio e fine nemmeno.'
                    : combacia
                      ? serveScelta
                        ? 'Combacia: resta una domanda sola.'
                        : 'Combacia: il pulsante qui sotto è attivo.'
                      : 'Non combacia ancora — controlla che sia proprio questo l\'elemento da cancellare.'}
                </p>
              </div>

              {serveScelta ? (
                <div className="dialogo-azioni">
                  {/* Neutro, non rosso: questo pulsante non cancella niente,
                      porta alla domanda. Il rosso resta all'unico pulsante che
                      esegue davvero, o smette di voler dire «ultima». */}
                  <button
                    type="button"
                    className="bottone"
                    onClick={() => setPasso('scelta')}
                    disabled={!combacia}
                  >
                    Continua <span aria-hidden="true">→</span>
                  </button>
                  <button type="button" className="bottone-piatto" onClick={onChiudi}>
                    Torna indietro
                  </button>
                </div>
              ) : (
                azioniFinali()
              )}
            </>
          ) : (
            /* --- Secondo passo: la domanda ---------------------------------- */
            <>
              {/* Promemoria di cosa è già stato fatto: il nome è stato
                  ricopiato al passo prima, e chi torna qui dopo aver cambiato
                  idea sulla scelta non deve chiedersi se l'ha fatto. */}
              <p className="aiuto passo-fatto">
                <span aria-hidden="true">✓</span> Nome ricopiato. Resta una domanda.
              </p>

              {impatto.collegate !== null && entitaCollegabile !== null && (
                <fieldset className="scelta-destino">
                  <legend>{domandaCollegate(impatto.collegate)}</legend>

                  {destiniCollegate({
                    collegate: impatto.collegate,
                    entita: entitaCollegabile,
                    nome: bersaglio.nome,
                  }).map((d) => (
                    <label
                      key={d.valore}
                      className={`destino destino-${d.valore.toLowerCase()}`}
                      // Il fondo si accende solo sulla riga scelta: due riquadri
                      // accesi contemporaneamente non direbbero qual è quello
                      // che parte.
                      aria-current={scelta === d.valore ? 'true' : undefined}
                    >
                      <input
                        type="radio"
                        name="destino-collegate"
                        value={d.valore}
                        checked={scelta === d.valore}
                        onChange={() => setScelta(d.valore)}
                        disabled={inCorso}
                      />

                      <span className="destino-corpo">
                        <span className="destino-titolo">
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            {d.valore === 'ELIMINA' ? CESTINO : CATENA_SPEZZATA}
                          </svg>
                          {d.titolo}
                        </span>

                        <span className="destino-effetto">{d.effetto}</span>

                        {/* Solo su un destino dei due, ed è il punto: se
                            comparisse su entrambi tornerebbero a sembrare due
                            varianti della stessa cosa. */}
                        {d.avvertimento !== null && (
                          <span className="destino-avvertimento">
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 16 16"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M8 2.2 1.6 13.3h12.8L8 2.2ZM8 6.4v3.2M8 11.6v.1" />
                            </svg>
                            {d.avvertimento}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </fieldset>
              )}

              {/* L'elenco qui sotto è quello del ramo selezionato: si ricarica
                  a ogni cambio di scelta. */}
              {elencoConseguenze(
                scelta === null ? 'Cosa comporta, scegliendo di conservarle' : 'Cosa comporta',
              )}

              {scelta === null && (
                <p className="aiuto nota-scelta-dopo" role="status">
                  Nessuna delle due è preselezionata: scegli quale dei due destini vuoi, e il
                  pulsante qui sotto dirà esattamente cosa sta per fare.
                </p>
              )}

              {azioniFinali()}
            </>
          )}
        </>
      )}
    </Dialogo>
  );
}

// -----------------------------------------------------------------------------
// Il punto da cui si parte
// -----------------------------------------------------------------------------

/**
 * Il riquadro rosso in fondo a un dialogo di modifica.
 *
 * Sta **in fondo**, oltre a «Salva modifiche», e non nella riga della tabella:
 * per arrivarci bisogna aprire la scheda di un elemento preciso e scorrere fino
 * al fondo, il che è già una decisione. Accanto a «Modifica» sarebbe a un clic
 * di distanza da una mano distratta, e sembrerebbe un'azione come le altre.
 */
export function ZonaPericolosa({
  spiegazione,
  onElimina,
}: {
  /** Cosa fare **invece**, in una riga: è la strada giusta nove volte su dieci. */
  spiegazione: string;
  onElimina: () => void;
}) {
  return (
    <div className="zona-pericolosa">
      <h3>
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 2.2 1.6 13.3h12.8L8 2.2ZM8 6.4v3.2M8 11.6v.1" />
        </svg>
        Zona pericolosa
      </h3>
      <p className="aiuto">{spiegazione}</p>
      <button type="button" className="bottone bottone-pericolo" onClick={onElimina}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {CESTINO}
        </svg>
        Elimina definitivamente
      </button>
    </div>
  );
}
